import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { PersistentStoreName } from 'stores/_utils/persistentStore/persistenceStoresCleanUpMap'
import { persistentInit } from 'stores/_utils/persistentStore/persistentStore'
import type {
  TActiveGateway,
  TActiveNode,
  TDevice,
  TLures,
  TNodeLog,
  TNodeServiceIssueSummary,
  TNodeStatus,
  TPlannedGateway,
  TPlannedNode,
} from '../types'
import { actions } from './actions/serviceCenter'
import { selectors } from './selectors/serviceCenter'

export type TEquipmentType = 'trap' | 'gateway' | 'dispenser' | 'repeater' | 'station' | 'external'

export type TNodeDeviceType = 'frost-fan' | 'weather' | 'leaf-wetness' | 'water-flow' | 'dendrometer' | 'soil'

export type NodeSwapReasons = routes.ServiceCenterNodeSwapReasonsGet.Response

export type NodeRemoveReasons = routes.ServiceCenterNodeRemoveReasonsGet.Response

export type TServiceCenterStore = {
  mapEquipmentStatuses: TNodeStatus[]
  mapEquipmentTypes: TEquipmentType[]
  mapEquipmentDevices: TNodeDeviceType[]
  devices: Record<string, TDevice>
  plannedNodes: Array<TPlannedNode | TPlannedGateway>
  activeNodes: Array<TActiveNode | TActiveGateway>
  nodeStatuses: Record<string, TNodeStatus>
  nodeLogs: Record<string, TNodeLog>
  mostRecentLogTime: string | null
  selectedPlannedEquipmentIds: string[]
  selectedActiveEquipmentIds: string[]
  serviceIssues: Record<string, TNodeServiceIssueSummary[]>
  swapReasons: NodeSwapReasons
  removeReasons: NodeRemoveReasons
  lures: TLures
  showIconDetailBadge: boolean
  offlineContents: {
    isBannerOpened: boolean
    lastTimeDownloaded: string | null
    downloadStatus: 'default' | 'success' | 'error' | string
  }
  propertyOverviewZoomLevel: number
}

const store = persistentInit<TServiceCenterStore>({
  initialState: {
    mapEquipmentStatuses: [],
    mapEquipmentTypes: [],
    mapEquipmentDevices: [],
    devices: {},
    plannedNodes: [],
    activeNodes: [],
    nodeStatuses: {},
    nodeLogs: {},
    mostRecentLogTime: null,
    selectedPlannedEquipmentIds: [],
    selectedActiveEquipmentIds: [],
    serviceIssues: {},
    swapReasons: {},
    removeReasons: {},
    lures: {},
    offlineContents: {
      isBannerOpened: false,
      lastTimeDownloaded: null,
      downloadStatus: 'default',
    },
    propertyOverviewZoomLevel: 10,
    showIconDetailBadge: true,
  },
  keysToPutInPersistentStorage: {
    mapEquipmentStatuses: true,
    mapEquipmentTypes: true,
    mapEquipmentDevices: true,
    devices: true,
    plannedNodes: true,
    activeNodes: true,
    nodeStatuses: true,
    nodeLogs: true,
    mostRecentLogTime: true,
    selectedPlannedEquipmentIds: false,
    selectedActiveEquipmentIds: false,
    serviceIssues: true,
    swapReasons: true,
    removeReasons: true,
    lures: true,
    offlineContents: true,
    propertyOverviewZoomLevel: false,
    showIconDetailBadge: false,
  },
  keysToPutInURL: {
    mapEquipmentStatuses: true,
    mapEquipmentTypes: true,
    mapEquipmentDevices: true,
    devices: false,
    plannedNodes: false,
    activeNodes: false,
    nodeStatuses: false,
    nodeLogs: false,
    mostRecentLogTime: false,
    selectedPlannedEquipmentIds: true,
    selectedActiveEquipmentIds: true,
    serviceIssues: false,
    swapReasons: false,
    removeReasons: false,
    lures: false,
    offlineContents: false,
    propertyOverviewZoomLevel: false,
    showIconDetailBadge: false,
  },
  storeName: PersistentStoreName.SERVICE_CENTER_STORE,
})

export const serviceCenterStore = {
  ...store,
  actions,
  selectors,
}
